import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { planDialogModel } from '../../lib/planGate';

const PLAN_LABEL = {
  standard: { en: 'Standard', ar: 'ستاندرد' },
  premium: { en: 'Premium', ar: 'بريميوم' },
  enterprise: { en: 'Enterprise', ar: 'إنتربرايز' },
};

// Shared dialog for every plan refusal (HTTP 402 plan_gate). Shows the server's
// sentence, an "Upgrade plan" primary action to plan_gate.upgrade_url, and a
// "Not now" close. When can_upgrade is false, only a close button. Never a status
// code.
export function PlanLimitDialog({ open, onOpenChange, gate, message }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (en, ar) => (language === 'ar' ? ar : en);

  const canUpgrade = gate?.can_upgrade !== false;
  const { upgradeUrl } = planDialogModel(gate);
  const required = gate?.required_plan;
  const planLabel = required && PLAN_LABEL[required]
    ? (language === 'ar' ? PLAN_LABEL[required].ar : PLAN_LABEL[required].en)
    : null;

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate(upgradeUrl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="plan-limit-dialog">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <Lock className="h-6 w-6 text-amber-600" />
          </div>
          <DialogTitle className="text-center" data-testid="plan-limit-title">
            {planLabel
              ? t(`Available on ${planLabel} and above`, `متاح في ${planLabel} وما فوق`)
              : t('Upgrade required', 'ترقية مطلوبة')}
          </DialogTitle>
          <DialogDescription className="text-center pt-1" data-testid="plan-limit-message">
            {message || t('This feature requires a higher plan.', 'تتطلب هذه الميزة خطة أعلى.')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2 sm:justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="plan-limit-close-btn"
          >
            {t('Not now', 'ليس الآن')}
          </Button>
          {canUpgrade && (
            <Button onClick={handleUpgrade} data-testid="plan-limit-upgrade-btn">
              <Sparkles className="mr-2 h-4 w-4" />
              {t('Upgrade plan', 'ترقية الخطة')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PlanLimitDialog;
